import { catalogs, DEFAULT_LOCALE, isLocale, type Locale, type MessageKey, type TranslateValues } from "@erp/i18n";
import {
  countryName,
  currencyName,
  formatAddressLines,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  formatRelative,
  type DateStyle,
  type PostalAddress,
} from "@erp/i18n/format";
import { createInstance } from "i18next";
import ICU from "i18next-icu";
import { useCallback, useMemo } from "react";
import { initReactI18next, useTranslation } from "react-i18next";
import { storage } from "./storage";

const STORAGE_KEY = "erp.locale";

function initialLocale(): Locale {
  const stored = storage.get(STORAGE_KEY);
  if (isLocale(stored)) return stored;
  const browser = navigator.language.slice(0, 2);
  return isLocale(browser) ? browser : DEFAULT_LOCALE;
}

export const i18n = createInstance();

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    lng: initialLocale(),
    fallbackLng: DEFAULT_LOCALE,
    resources: { fr: { translation: catalogs.fr }, en: { translation: catalogs.en } },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

document.documentElement.lang = i18n.language;
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
  storage.set(STORAGE_KEY, lng);
});

export type T = (key: MessageKey, values?: TranslateValues) => string;

/** Typed translation hook: unknown keys fail at compile time. */
export function useT(): T {
  const { t } = useTranslation();
  return useCallback<T>((key, values) => t(key, values ?? {}), [t]);
}

export function useLocale(): [Locale, (locale: Locale) => void] {
  const { i18n: instance } = useTranslation();
  const locale = isLocale(instance.language) ? instance.language : DEFAULT_LOCALE;
  return [locale, (next) => void instance.changeLanguage(next)];
}

/** Locale-bound formatters; never format numbers, money or dates by hand. */
export function useFormat() {
  const [locale] = useLocale();
  return useMemo(
    () => ({
      money: (cents: number, currency = "EUR") => formatMoney(cents, currency, locale),
      number: (value: number, options?: Intl.NumberFormatOptions) => formatNumber(value, locale, options),
      percent: (ratio: number, digits?: number) => formatPercent(ratio, locale, digits),
      date: (value: Date | string, style?: DateStyle) => formatDate(value, locale, style),
      relative: (value: Date | string) => formatRelative(value, locale),
      country: (code: string) => countryName(code, locale),
      currency: (code: string) => currencyName(code, locale),
      address: (address: PostalAddress) => formatAddressLines(address, locale),
    }),
    [locale],
  );
}

/** Translates a validation message that is itself an i18n key (see @erp/shared schemas). */
export function translateFieldError(t: T, message: string | undefined): string | undefined {
  if (!message) return undefined;
  return message.startsWith("errors.") ? t(message as MessageKey) : t("errors.validation");
}
